import { format, FormatSpecifier } from 'd3-format'

export const FORMAT_TYPES = {
  NONE: 'none',
  NUMBER: 'number',
  NUMBER_SHORT: 'number_short',
  NUMBER_SHORTER: 'number_shorter',
  PERCENT: 'percent',
  PERCENT_SHORT: 'percent_short',
  PERCENT_SHORTER: 'percent_shorter'
}

function isBetweenNegativeOneAndOne (value) {
  return (value < 1) && (value > -1)
}

function isReallyLargeOrReallySmall (value) {
  return value <= -1e15 || value >= 1e15 // +/-1 quadrillion
}

function formatSI (value) {
  // Uses International System of Units (SI) except for B instead of G
  return value.replace(/G/, 'B')
}

class FormatService {
  formatValue (value, format) {
    switch (format) {
      case FORMAT_TYPES.NUMBER:
        return this.formatNumber(value)
      case FORMAT_TYPES.NUMBER_SHORT:
        return this.formatNumberShort(value)
      case FORMAT_TYPES.NUMBER_SHORTER:
        return this.formatNumberShorter(value)
      case FORMAT_TYPES.PERCENT:
        return this.formatPercent(value)
      case FORMAT_TYPES.PERCENT_SHORT:
        return this.formatPercentShort(value)
      case FORMAT_TYPES.PERCENT_SHORTER:
        return this.formatPercentShorter(value)
      default:
        return value
    }
  }

  formatShortenedValue (value, format) {
    format = ((format === FORMAT_TYPES.NUMBER) || (format === FORMAT_TYPES.NONE)) ? FORMAT_TYPES.NUMBER_SHORT : format
    format = (format === FORMAT_TYPES.PERCENT) ? FORMAT_TYPES.PERCENT_SHORT : format
    return this.formatValue(value, format)
  }

  formatPercent (value) {
    // Handle really small percents
    if (value > 0 && value < 0.00001) {
      return '< 0.001%'
    }

    const formatSpec = new FormatSpecifier({
      comma: true,
      precision: 3,
      trim: true,
      type: '%'
    })
    return format(formatSpec)(value)
  }

  formatPercentShort (value) {
    // Handle really small percents
    if (value > 0 && value < 0.0001) {
      return '<0.01%'
    }

    // Handle really big percents
    if (value >= 10) {
      value = value * 100
      return this.formatNumberShort(value) + '%'
    }

    const formatSpec = new FormatSpecifier({
      precision: 2,
      trim: true,
      type: '%'
    })
    return format(formatSpec)(value)
  }

  formatPercentShorter (value) {
    // Handle really small percents
    if (value > 0 && value < 0.001) {
      return '<0.1%'
    }

    // Handle really big percents
    if (value >= 10) {
      value = value * 100
      return this.formatNumberShorter(value) + '%'
    }

    const formatSpec = new FormatSpecifier({
      precision: 1,
      trim: true,
      type: '%'
    })
    return format(formatSpec)(value)
  }

  formatPercentShortFromFraction (numerator, denominator) {
    return this.formatPercentShort(numerator / denominator)
  }

  formatNumber (value) {
    // Handle really small numbers
    if (value > 0 && value < 0.00001) {
      return '< 0.00001'
    }

    // For numbers between -1 and 1, use significant digits, otherwise use fixed
    const type = isBetweenNegativeOneAndOne(value) ? 'r' : 'f'

    const formatSpec = new FormatSpecifier({
      comma: true,
      precision: 3,
      trim: true,
      type
    })
    return format(formatSpec)(value)
  }

  formatNumberShort (value) {
    // Handle small numbers
    if (value > 0 && value < 0.01) {
      return '<0.01'
    }

    // Use significant digits + SI for shortening most numbers
    let type = 's'
    if (isBetweenNegativeOneAndOne(value)) {
      // Use fixed position for small numbers
      type = 'f'
    } else if (isReallyLargeOrReallySmall(value)) {
      // Use exponent notation for really large or really small numbers
      type = 'e'
    }

    const formatSpec = new FormatSpecifier({
      precision: isBetweenNegativeOneAndOne(value) ? 2 : 3,
      trim: true,
      type
    })

    return formatSI(format(formatSpec)(value))
  }

  formatNumberShorter (value) {
    // Handle small numbers
    if (value > 0 && value < 0.1) {
      return '<0.1'
    }

    // Use significant digits + SI for shortening most numbers
    let type = 's'
    if (isBetweenNegativeOneAndOne(value)) {
      // Use fixed position for small numbers
      type = 'f'
    } else if (isReallyLargeOrReallySmall(value)) {
      // Use exponent notation for really large or really small numbers
      type = 'e'
    }

    let precision
    if (isBetweenNegativeOneAndOne(value)) {
      precision = 1
    } else {
      const roundedToInteger = format('d')(value)
      const digitLength = Math.abs(roundedToInteger).toString().length
      precision = (digitLength % 3 === 0) ? 3 : 2
    }

    const formatSpec = new FormatSpecifier({
      precision,
      trim: true,
      type
    })

    return formatSI(format(formatSpec)(value))
  }

  formatNumberRoundedToInteger (value) {
    const formatSpec = new FormatSpecifier({
      comma: true,
      type: 'd'
    })
    return format(formatSpec)(value)
  }
}

export const formatService = new FormatService()
